(() => {
  const script = document.currentScript || document.getElementById('MoriPostHogScript');

  if (!script || window.__moriPostHogInitialized) return;

  const projectToken = script.dataset.projectToken;
  const apiHost = script.dataset.apiHost;

  if (!projectToken || !apiHost) return;

  // Keep local dev, theme editor and preview traffic out of the production project.
  const hostname = window.location.hostname;
  const isInternalHost =
    window.Shopify?.designMode ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.myshopify.com') ||
    hostname.endsWith('.shopifypreview.com');
  if (isInternalHost) return;

  /*
   * Internal traffic on the live domain.
   *
   * Local dev, the theme editor and previews are already excluded above, but testing on
   * drinkmori.co.uk itself is not. Visit any page with ?mori_internal=1 once per browser to
   * tag that device for good, and ?mori_internal=0 to untag it. Events still arrive, so the
   * sessions stay debuggable, but they carry $internal_or_test_user so PostHog's
   * "Filter out internal and test users" toggle drops them.
   *
   * This is a registered super property rather than a person property on purpose: the SDK
   * runs person_profiles: 'identified_only', so an anonymous browser has no person profile
   * for a person-property filter to match against.
   */
  const INTERNAL_STORAGE_KEY = 'mori_internal';

  const readInternalFlag = () => {
    let flag = null;

    try {
      flag = window.localStorage.getItem(INTERNAL_STORAGE_KEY);
    } catch (error) {
      flag = null;
    }

    const requested = new URLSearchParams(window.location.search).get(INTERNAL_STORAGE_KEY);

    if (requested === '1' || requested === '0') {
      flag = requested === '1' ? 'true' : null;

      try {
        if (flag) window.localStorage.setItem(INTERNAL_STORAGE_KEY, 'true');
        else window.localStorage.removeItem(INTERNAL_STORAGE_KEY);
      } catch (error) {
        /* Private browsing: the tag lasts for this page only. */
      }
    }

    return flag === 'true';
  };

  const isInternalVisitor = readInternalFlag();

  /*
   * Hero backdrop A/B test arm.
   *
   * mori-hero.liquid settles the arm in a parser-blocking inline script, so it is
   * already on window by the time this deferred script runs. Registering it as a
   * super property means every later event in the session carries the arm, which is
   * what makes the funnel from homepage to purchase breakable down by variant.
   */
  const heroExperiment = window.__moriHeroExperiment;

  /*
   * Attribution.
   *
   * PostHog only puts utm_* on the landing pageview, so later pages and return visits
   * lose the source. mori-analytics.js (loaded just before this script) keeps first-touch
   * and last-touch in the mori_attribution cookie, writes them onto the cart for the order,
   * and exposes the same values as window.__moriAttribution. They go on every event as
   * attributed_* so PostHog and the order attributes always agree. PostHog's own utm_* are
   * left alone because Web Analytics channel reports depend on them.
   */
  const ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'first_utm_source',
    'first_utm_medium',
    'first_utm_campaign',
    'referrer_domain',
    'landing_page',
  ];
  const ATTRIBUTION_PROPERTIES = [...ATTRIBUTION_KEYS.map((key) => `attributed_${key}`), 'attribution_summary'];

  const readAttribution = () => {
    const attribution = window.__moriAttribution || {};
    const properties = {};
    ATTRIBUTION_KEYS.forEach((key) => {
      if (attribution[key]) properties[`attributed_${key}`] = attribution[key];
    });
    if (attribution.attribution_summary) properties.attribution_summary = attribution.attribution_summary;
    return properties;
  };

  const storefrontProperties = {
    integration: 'shopify_theme',
    shopify_template: script.dataset.template,
    ...readAttribution(),
    ...(isInternalVisitor ? { $internal_or_test_user: 'true' } : {}),
    ...(heroExperiment
      ? {
          hero_experiment: heroExperiment.key,
          hero_experiment_arm: heroExperiment.arm,
          // PostHog reads the variant off $feature/<flag key> when scoring the experiment.
          ['$feature/' + heroExperiment.key]: heroExperiment.arm,
        }
      : {}),
  };
  const sessionRecordingEnabled = script.dataset.sessionRecording === 'true';
  let clientReady = false;
  let pageviewCaptured = false;

  const analyticsProcessingAllowed = () => {
    const customerPrivacy = window.Shopify?.customerPrivacy;

    if (!customerPrivacy) return true;

    try {
      return customerPrivacy.analyticsProcessingAllowed();
    } catch (error) {
      return false;
    }
  };

  const syncConsent = () => {
    if (!clientReady || !window.posthog) return;

    if (!analyticsProcessingAllowed()) {
      window.posthog.opt_out_capturing();
      return;
    }

    // Only opt in when needed, and without the extra $opt_in event on every page.
    if (window.posthog.has_opted_out_capturing()) window.posthog.opt_in_capturing({ captureEventName: false });
    // register() persists in the PostHog cookie, so drop attribution values that no longer apply.
    ATTRIBUTION_PROPERTIES.forEach((key) => {
      if (!storefrontProperties[key]) window.posthog.unregister(key);
    });
    window.posthog.register(storefrontProperties);

    /*
     * Also feed the person-property cohort, for the internal sessions that do get identified.
     *
     * setPersonProperties has to be in the stub's method list above. The `loaded` callback can
     * fire while window.posthog is still the stub, and a missing method threw there, which
     * skipped the rest of this function: no $pageview, no experiment exposure and no
     * mori:posthog-ready for mori-analytics.js. Internal sessions only, so it stayed hidden.
     */
    if (isInternalVisitor) window.posthog.setPersonProperties({ $internal_or_test_user: 'true' });

    if (!pageviewCaptured) {
      window.posthog.capture('$pageview', storefrontProperties);
      pageviewCaptured = true;

      /*
       * The hero arm is settled locally before flags load, so it never emits the
       * $feature_flag_called event PostHog normally treats as an experiment
       * exposure. The hero experiment is configured to read this event instead.
       */
      if (heroExperiment) window.posthog.capture('hero_experiment_exposed', storefrontProperties);

      // mori-analytics.js waits for this before sending page-level commerce events.
      window.__moriPostHogReady = true;
      document.dispatchEvent(new CustomEvent('mori:posthog-ready'));
    }
  };

  const installPostHog = () => {
    if (window.__moriPostHogInitialized) return;

    window.__moriPostHogInitialized = true;

    /* PostHog's official HTML loader, kept local so theme settings can control initialization. */
    ((documentObject, posthogObject) => {
      let index;
      let method;
      let scriptElement;
      let targetScript;

      if (posthogObject.__SV) return;

      window.posthog = posthogObject;
      posthogObject._i = [];
      posthogObject.init = (token, config, name) => {
        const addMethod = (target, methodName) => {
          const parts = methodName.split('.');
          let methodTarget = target;
          let finalMethod = methodName;

          if (parts.length === 2) {
            methodTarget = target[parts[0]];
            finalMethod = parts[1];
          }

          methodTarget[finalMethod] = (...args) => {
            target.push([methodName, ...args]);
          };
        };

        scriptElement = documentObject.createElement('script');
        scriptElement.type = 'text/javascript';
        scriptElement.crossOrigin = 'anonymous';
        scriptElement.async = true;
        scriptElement.src = `${config.api_host.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/array.js`;
        targetScript = documentObject.getElementsByTagName('script')[0];
        targetScript.parentNode.insertBefore(scriptElement, targetScript);

        const queue = name ? (posthogObject[name] = []) : posthogObject;
        queue.people = queue.people || [];
        queue.toString = (calledAsPeople) => {
          let output = 'posthog';
          if (name) output += `.${name}`;
          if (!calledAsPeople) output += ' (stub)';
          return output;
        };
        queue.people.toString = () => `${queue.toString(true)}.people (stub)`;

        const methods =
          'capture identify alias people.set people.set_once setPersonProperties set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys getNextSurveyStep onSessionId'.split(
            ' '
          );

        for (index = 0; index < methods.length; index += 1) {
          method = methods[index];
          addMethod(queue, method);
        }

        posthogObject._i.push([token, config, name]);
      };
      posthogObject.__SV = 1;
    })(document, window.posthog || []);

    const initiallyAllowed = analyticsProcessingAllowed();

    window.posthog.init(projectToken, {
      api_host: apiHost,
      ui_host: 'https://eu.posthog.com',
      defaults: '2026-05-30',
      person_profiles: 'identified_only',
      autocapture: true,
      capture_pageview: false,
      capture_pageleave: true,
      enable_heatmaps: true,
      // Flags are on for experiments read at a click (assets/mori-checkout-experiment.js). The theme
      // runs no surveys or support widget; loading them only added noise to every event.
      disable_surveys: true,
      disable_conversations: true,
      disable_session_recording: !sessionRecordingEnabled,
      session_recording: {
        maskAllInputs: true,
      },
      opt_out_capturing_by_default: !initiallyAllowed,
      before_send: (event) => (analyticsProcessingAllowed() ? event : null),
      loaded: () => {
        clientReady = true;
        syncConsent();
      },
    });
  };

  document.addEventListener('visitorConsentCollected', syncConsent);

  if (window.Shopify?.customerPrivacy) {
    installPostHog();
    return;
  }

  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], installPostHog);
    return;
  }

  installPostHog();
})();
